/**
 * Viewport media-query hooks. `useMediaQuery` is the generic primitive;
 * `useWideViewport` is the named question the app actually asks.
 *
 * useWideViewport — reactive "is the window wide enough for the left-rail
 * layout?" Single source of truth for the responsive navigation:
 *   - wide (≥ RAIL_MIN_WIDTH) → left rail (PC / tablet)
 *   - narrow (< RAIL_MIN_WIDTH) → bottom tab bar (mobile)
 *
 * Updates live on window resize via matchMedia. SSR / no-matchMedia default is
 * `false` (mobile-first bottom bar) so server render + unit tests are stable
 * unless they stub matchMedia.
 */
import { useEffect, useState } from 'react';

/** px at/above which the nav becomes a left rail; below it is a bottom bar. */
export const RAIL_MIN_WIDTH = 768;

/**
 * Skallets faste mål (#153), delt av InteractiveMode og ruter som fyller
 * høyden selv (AssistantRoute).
 *
 * NAV_CLEARANCE — det pillen og assistentknappen tar nederst: 28 px fra
 * bunnen + 64 px høye + 20 px luft. Gjelder telefonen alltid, og den brede
 * skinnen der assistentknappen vises.
 *
 * Høyden på skallets topp (SHELL_HEADER_HEIGHT) er fjernet (#158): bare
 * AssistantRoute trakk den fra, og den tegner nå sin egen topp.
 */
export const NAV_CLEARANCE = 112;

/** Reactive `window.matchMedia(query).matches`; `false` without matchMedia. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange(); // sync in case the width changed between first render and effect
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

export function useWideViewport(): boolean {
  return useMediaQuery(`(min-width: ${RAIL_MIN_WIDTH}px)`);
}
